import React, { useMemo, useState } from "react";
import {
  Combobox,
  ComboboxItem,
  ComboboxPopover,
  ComboboxProvider,
} from "@ariakit/react";
import cn from "classnames";
import { useController, RegisterOptions } from "react-hook-form";
import { useFormControl } from "../FormControl";

import styles from "./SearchableSelect.module.scss";

interface SearchableSelectOption {
  value: string;
  label: string;
}

export interface SearchableSelectProps {
  options: Array<SearchableSelectOption>;
  placeholder?: string;
  rules?: RegisterOptions;
  className?: string;
  defaultValue?: string;
}

const SearchableSelect = ({
  options,
  placeholder,
  rules,
  className,
  defaultValue = "",
}: SearchableSelectProps) => {
  const { name, error } = useFormControl();
  const { field } = useController({ name, rules, defaultValue });
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const selectedLabel = useMemo(
    () => options.find((o) => o.value === field.value)?.label || "",
    [options, field.value],
  );

  const displayValue = open ? searchValue : selectedLabel;

  const filteredOptions = useMemo(() => {
    if (!searchValue) return options;
    const lower = searchValue.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(lower));
  }, [options, searchValue]);

  return (
    <ComboboxProvider
      open={open}
      setOpen={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) {
          setSearchValue("");
        }
      }}
      value={displayValue}
      setValue={setSearchValue}
      setSelectedValue={(value) => {
        const option = options.find((o) => o.label === value);
        if (option) {
          field.onChange(option.value);
        }
      }}
    >
      <Combobox
        id={name}
        placeholder={placeholder}
        autoSelect
        disabled={field.disabled}
        className={cn(styles.input, { [styles.hasError]: !!error }, className)}
        onBlur={field.onBlur}
      />
      <ComboboxPopover sameWidth gutter={4} className={styles.popover}>
        {filteredOptions.map((option) => (
          <ComboboxItem
            key={option.value}
            value={option.label}
            focusOnHover
            className={styles.item}
          />
        ))}
        {filteredOptions.length === 0 ? (
          <div className={styles.noResults}>No results found</div>
        ) : null}
      </ComboboxPopover>
    </ComboboxProvider>
  );
};

export default SearchableSelect;
